import { cn } from '../../lib/cn';
import {
  formatScoreOutOf,
  formatSigned,
  type ScaleAcronym,
} from '../../utils/scoreColor';
import {
  getLevelState,
  getLevelVariant,
  LADDER_ALIGN_SCORES,
  SCALE_LEVELS,
} from '../../utils/scaleLevels';
import { ScaleColumnFooter } from './ScaleColumnFooter';
import { ScaleLevelBox } from './ScaleLevelBox';

export interface ScaleColumnProps {
  acronym: ScaleAcronym;
  primaryName: 'Truth' | 'Engagement' | 'Integration';
  fullName: string;
  score: number;
  justification: string;
  emphasized?: boolean;
  /** -- Tailwind order classes for responsive column order */
  orderClassName: string;
  /**
   * When true (default), insert invisible spacers for missing align rows
   * so zeros line up with sibling columns (desktop side-by-side).
   */
  alignRows?: boolean;
}

export function ScaleColumn({
  acronym,
  primaryName,
  fullName,
  score,
  justification,
  emphasized = false,
  orderClassName,
  alignRows = true,
}: ScaleColumnProps) {
  const levels = SCALE_LEVELS[acronym];
  const negativeMode = score < 0;
  const activeLabel =
    levels.find((l) => l.score === score)?.label ?? formatSigned(score);
  const aria = `${primaryName} (${acronym}), ${fullName}, score ${formatScoreOutOf(
    score,
    acronym
  )}, ${activeLabel}`;

  const rowScores = alignRows
    ? LADDER_ALIGN_SCORES
    : levels.map((l) => l.score);

  return (
    <div
      role="group"
      aria-label={aria}
      className={cn(
        'flex min-w-0 flex-col gap-2',
        emphasized && 'md:px-0.5',
        orderClassName
      )}
    >
      <div className="flex flex-col gap-1.5 sm:gap-2">
        {rowScores.map((levelScore) => {
          const level = levels.find((l) => l.score === levelScore);
          if (!level) {
            // -- Spacer so ETS +4 sits above SES/EIS +3 and zeros share a row
            return (
              <div
                key={`${acronym}-pad-${levelScore}`}
                aria-hidden="true"
                className="invisible pointer-events-none flex w-full items-center gap-2 rounded-md border px-2.5 py-2 sm:gap-3 sm:px-3 sm:py-2.5"
              >
                <span className="shrink-0 font-display text-lg font-black leading-none sm:text-xl md:text-2xl">
                  {formatSigned(levelScore)}
                </span>
                <span className="min-w-0 flex-1 text-left text-[11px] font-bold leading-snug sm:text-xs md:text-sm">
                  Spacer
                </span>
              </div>
            );
          }

          const state = getLevelState(score, level.score);
          const variant = getLevelVariant(acronym, level.score);
          return (
            <ScaleLevelBox
              key={`${acronym}-${level.score}`}
              score={level.score}
              label={level.label}
              state={state}
              variant={variant}
              negativeMode={negativeMode}
              justification={state === 'active' ? justification : undefined}
            />
          );
        })}
      </div>

      <ScaleColumnFooter
        acronym={acronym}
        primaryName={primaryName}
        fullName={fullName}
        score={score}
        emphasized={emphasized}
      />
    </div>
  );
}
